import { Button, Checkbox, FormControlLabel, Paper, Switch, Typography } from "@material-ui/core"
import ChevronDownIcon from "@material-ui/icons/ExpandLess"
import ChevronUpIcon from "@material-ui/icons/ExpandMore"
import MapIcon from "@material-ui/icons/Map"
import SatelliteIcon from "@material-ui/icons/Satellite"
import { withStyles } from "@material-ui/styles"
import mapboxgl from "mapbox-gl/dist/mapbox-gl"
import MapboxWorker from "mapbox-gl/dist/mapbox-gl-csp-worker"
import React, { useCallback, useEffect, useRef, useState } from "react"
import * as ReactMapGL from "react-map-gl"
import { useMediaQuery } from "react-responsive"
import turf from "turf"
import outdoorStyle from "../../style/mapStyles/outdoor.json"
import streetStyle from "../../style/mapStyles/street.json"
import { USER_ALERT_TYPES } from "../../util/constants"
import { getDisplayName } from "../../util/deviceHelpers"
import { FILTER_TYPE } from "../../util/filters/filterConstants"
import { FilterManager } from "../../util/filters/filterManager"
import CustomTableFilters from "../Common/Table/CustomTableFilters"
import DeviceView from "./DeviceView"
import {
  clusterCountLayer,
  clusteredPointLayerBatteryWarning,
  clusteredPointLayerBatteryWarningCircle,
  clusteredPointLayerSpypoint,
  clusteredPointLayerSpypointCircle,
  clusteredPointLayerSyncError,
  clusteredPointLayerSyncErrorCircle,
  clusteredPointLayerWarning,
  clusteredPointLayerWarningText,
  clusterLayer,
  unclusteredPointLayer,
  unclusteredPointLayerBatteryWarning,
  unclusteredPointLayerBatteryWarningCircle,
  unclusteredPointLayerCount,
  unclusteredPointLayerCountCircle,
  unclusteredPointLayerInsideText,
  unclusteredPointLayerSpypoint,
  unclusteredPointLayerSpypointCircle,
  unclusteredPointLayerSyncError,
  unclusteredPointLayerSyncErrorCircle,
  unclusteredPointLayerWarning,
  unclusteredPointLayerWarningText,
} from "./Layers"

mapboxgl.workerClass = MapboxWorker

const styles = (theme) => ({
  mapActionMenu: {
    position: "absolute",
    top: 10,
    left: "2vh",
    zIndex: 100,
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    columnGap: theme.spacing(2),
  },
  centerIcons: {
    display: "flex",
    alignItems: "center",
    padding: 5,
    flexDirection: "row",
  },
  legend: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
  },
  collapsedGroup: {
    display: "flex",
    flexDirection: "column",
  },
  filterContainer: {
    marginTop: 5,
    marginLeft: 5,
    marginRight: 5,
    padding: "10px",
    width: "100%",
  },
  options: {
    display: "flex",
    flexDirection: "column",
  },
  topElements: {
    display: "flex",
    justifyContent: "center",
    minWidth: 310,
    alignItems: "center",
  },
  layers: {
    margin: "15px",
    padding: "10px",
    border: "2px solid #000",
    borderRadius: "8px",
    textAlign: "center",
  },
  collapseButton: {
    height: "32px",
    width: "32px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    color: "#0093C5",
    border: "none",
    backgroundColor: "white",
    zIndex: 1000,
    cursor: "pointer",
    marginLeft: 50,
  },
})

const MAPBOX_TOKEN =
  "pk.eyJ1IjoicGF0cmlja2NvbWJlIiwiYSI6ImNrbHR5eTF0ZTBkbGMycXF4bXc2dWF5bHQifQ.wCfYeiV37t7c9yqZcGQLgQ"

const MapView = (props) => {
  const { classes, points, hideDropdown, setTab } = props
  const isMobile = useMediaQuery({ query: "(max-width: 740px)" })
  const [viewState, setViewState] = useState({})
  const [mapStyle, setMapStyle] = useState(streetStyle)
  const [styleChecked, setStyleChecked] = useState(true)
  const [popupInfo, setPopupInfo] = useState(null)
  const [collapseLegend, setCollapseLegend] = useState(isMobile)
  const filterManagerRef = useRef(null)
  if (!filterManagerRef.current) {
    filterManagerRef.current = FilterManager.Builder.addFilter(FILTER_TYPE.DEVICE.GROUPS)
      .addFilter(FILTER_TYPE.DEVICE.ID)
      .addFilter(FILTER_TYPE.DEVICE.NAME)
      .addFilter(FILTER_TYPE.ALERT.TYPE)
      .addFilter(FILTER_TYPE.DEVICE.LAST_SEEN)
      .addFilter(FILTER_TYPE.DEVICE.LOCATION_NAME)
      .build()
  }
  const filterManager = filterManagerRef.current
  const [geoData, setGeoData] = useState({})
  const [filteredPoints, setFilteredPoints] = useState(points)
  const [layers, setLayers] = useState({
    deviceInsightWarning: {
      id: "deviceInsightWarning",
      name: "No Recent Insights",
      visible: false,
    },
    displaySyncWarning: {
      id: "displaySyncWarning",
      name: "No Recent Camera Triggers",
      visible: false,
    },
    spypointCableError: { id: "spypointCableError", name: "Spypoint Cable Error", visible: false },
    batteryLevelWarning: {
      id: "batteryLevelWarning",
      name: "Battery Level Warning",
      visible: false,
    },
    insightCount: { id: "insightCount", name: "Insight Count", visible: false },
  })
  const [mapRef, setMapRef] = useState(null)

  const onLoad = useCallback(() => {
    const features = filteredPoints.map((p) => {
      const lastWeek = new Date()
      lastWeek.setDate(lastWeek.getDate() - 7) // Subtract 7 days
      return {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [p.longitude, p.latitude],
        },
        properties: {
          insightCount: p.insightMetrics ? p.insightMetrics.count : 0,
          insightCountText: p.insightMetrics
            ? p.insightMetrics.count > 999
              ? ">999"
              : p.insightMetrics.count
            : 0,
          longitude: p.longitude,
          latitude: p.latitude,
          deviceName: p.device && getDisplayName(p.device),
          location: p.device?.location_name,
          deviceId: p.device?.device_id,
          lastSeen: p.device?.last_seen,
          insightWarning:
            p.alerts?.has(USER_ALERT_TYPES.NO_RECENT_INSIGHTS) &&
            Math.floor(
              (Date.now() - new Date(p.device?.last_seen).valueOf()) / (1000 * 60 * 60 * 24),
            ) >= 7,
          spypointCableError: p.alerts?.has(USER_ALERT_TYPES.SPYPOINT_ERROR),
          syncWarning: p.alerts?.has(USER_ALERT_TYPES.NO_RECENT_CAMERA_TRIGGERS),
          batteryLevelWarning: p.alerts?.has(USER_ALERT_TYPES.LOW_BATTERY),
          displayInsights: layers.insightCount.visible,
          displayInsightWarning: layers.deviceInsightWarning.visible,
          displaySpypointCableError: layers.spypointCableError.visible,
          displaySyncWarning: layers.displaySyncWarning.visible,
          displayBatteryLevelWarning: layers.batteryLevelWarning.visible,
          device_battery: p.device_battery,
          mostRecentClassName: p.insightMetrics?.mostRecentInsight
            ? p.insightMetrics.mostRecentInsight.class_name
            : null,
          mostRecentTimestamp: p.insightMetrics?.mostRecentInsight
            ? p.insightMetrics.mostRecentInsight.time_stamp
            : null,
          lastOnDeviceTimestamp: p.insightMetrics?.mostRecentOnDeviceInsight
            ? p.insightMetrics.mostRecentOnDeviceInsight.file_ctime
            : null,
        },
      }
    })
    setGeoData({
      type: "FeatureCollection",
      features,
    })
  }, [
    filteredPoints,
    layers.batteryLevelWarning.visible,
    layers.deviceInsightWarning.visible,
    layers.displaySyncWarning.visible,
    layers.insightCount.visible,
    layers.spypointCableError.visible,
  ])

  useEffect(() => {
    if (mapRef) {
      onLoad()
    }
  }, [mapRef, onLoad])

  useEffect(() => {
    if (mapRef && geoData?.features && geoData.features.length > 0) {
      const [minLng, minLat, maxLng, maxLat] = turf.bbox(geoData)

      mapRef.loadImage("images/sd-card-icon.png", (error, image) => {
        if (error) throw error
        if (!mapRef.hasImage("spypoint-cable-error")) {
          mapRef.addImage("spypoint-cable-error", image)
        }
      })

      mapRef.loadImage("images/no-image-photography-icon.png", (error, image) => {
        if (error) throw error
        if (!mapRef.hasImage("no-image")) {
          mapRef.addImage("no-image", image)
        }
      })

      mapRef.loadImage("images/low-battery-icon.png", (error, image) => {
        if (error) throw error
        if (!mapRef.hasImage("low-battery")) {
          mapRef.addImage("low-battery", image)
        }
      })

      mapRef.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { padding: isMobile ? 30 : 150, duration: 1000, maxZoom: 10 },
      )

      if (setTab) {
        mapRef.on("mouseover", clusterLayer.id, () => {
          mapRef.getCanvas().style.cursor = "pointer"
        })
        mapRef.on("mouseleave", clusterLayer.id, () => {
          mapRef.getCanvas().style.cursor = ""
        })
        mapRef.on("mouseover", unclusteredPointLayer.id, () => {
          mapRef.getCanvas().style.cursor = "pointer"
        })
        mapRef.on("mouseleave", unclusteredPointLayer.id, () => {
          mapRef.getCanvas().style.cursor = ""
        })
      }
    }
  }, [geoData, isMobile, setTab, mapRef])

  const onClick = (event) => {
    if (mapRef) {
      if (event.features && event.features.length > 0 && setTab) {
        const feature = event.features[0]
        if (feature.layer.id === clusterLayer.id) {
          const clusterId = feature.properties.cluster_id

          const mapboxSource = mapRef.getSource("cameras")

          mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) {
              return
            }
            mapRef.easeTo({
              center: feature.geometry.coordinates,
              zoom,
              duration: 1000,
            })
          })
        } else if (
          feature.layer.id === unclusteredPointLayer.id ||
          feature.layer.id === unclusteredPointLayerInsideText.id ||
          feature.layer.id === unclusteredPointLayerCountCircle.id ||
          feature.layer.id === unclusteredPointLayerCount.id
        ) {
          setPopupInfo(feature.properties)
        }
      }
    }
  }

  const toggleLayerVisibility = (layerId) => {
    const updatedLayers = { ...layers }
    updatedLayers[layerId] = { ...updatedLayers[layerId], visible: !updatedLayers[layerId].visible }
    setLayers(updatedLayers)
  }

  const handleStyleChange = () => {
    const checked = !styleChecked
    if (checked) {
      setMapStyle(streetStyle)
    } else {
      setMapStyle(outdoorStyle)
    }
    setStyleChecked(checked)
  }

  const handleMove = (viewState) => {
    setViewState(viewState)
  }

  return (
    <ReactMapGL.Map
      {...viewState}
      onMove={(evt) => handleMove(evt.viewState)}
      onLoad={(event) => setMapRef(event.target)}
      style={{ width: "100%", height: "100%" }}
      interactiveLayerIds={[clusterLayer.id, unclusteredPointLayer.id]}
      mapboxAccessToken={MAPBOX_TOKEN}
      renderWorldCopies={false}
      mapStyle={mapStyle}
      onClick={onClick}
      onRender={(event) => event.target.resize()}
    >
      <ReactMapGL.Source
        id="cameras"
        type="geojson"
        data={geoData}
        cluster={true}
        clusterMaxZoom={14}
        clusterRadius={35}
        clusterProperties={{
          insightWarning: ["any", ["get", "insightWarning"]],
          displayInsightWarning: ["any", ["get", "displayInsightWarning"]],
          spypointCableError: ["any", ["get", "spypointCableError"]],
          displaySpypointCableError: ["any", ["get", "displaySpypointCableError"]],
          syncWarning: ["any", ["get", "syncWarning"]],
          displaySyncWarning: ["any", ["get", "displaySyncWarning"]],
          batteryLevelWarning: ["any", ["get", "batteryLevelWarning"]],
          displayBatteryLevelWarning: ["any", ["get", "displayBatteryLevelWarning"]],
        }}
      >
        <ReactMapGL.Layer {...clusterLayer} />
        <ReactMapGL.Layer {...clusterCountLayer} />
        <ReactMapGL.Layer {...clusteredPointLayerWarning} />
        <ReactMapGL.Layer {...clusteredPointLayerWarningText} />
        <ReactMapGL.Layer {...clusteredPointLayerSpypointCircle} />
        <ReactMapGL.Layer {...clusteredPointLayerSpypoint} />
        <ReactMapGL.Layer {...clusteredPointLayerSyncErrorCircle} />
        <ReactMapGL.Layer {...clusteredPointLayerSyncError} />
        <ReactMapGL.Layer {...clusteredPointLayerBatteryWarningCircle} />
        <ReactMapGL.Layer {...clusteredPointLayerBatteryWarning} />
        <ReactMapGL.Layer {...unclusteredPointLayer} />
        <ReactMapGL.Layer {...unclusteredPointLayerInsideText} />
        <ReactMapGL.Layer {...unclusteredPointLayerCountCircle} />
        <ReactMapGL.Layer {...unclusteredPointLayerCount} />
        <ReactMapGL.Layer {...unclusteredPointLayerWarning} />
        <ReactMapGL.Layer {...unclusteredPointLayerWarningText} />
        <ReactMapGL.Layer {...unclusteredPointLayerSpypointCircle} />
        <ReactMapGL.Layer {...unclusteredPointLayerSpypoint} />
        <ReactMapGL.Layer {...unclusteredPointLayerSyncErrorCircle} />
        <ReactMapGL.Layer {...unclusteredPointLayerSyncError} />
        <ReactMapGL.Layer {...unclusteredPointLayerBatteryWarningCircle} />
        <ReactMapGL.Layer {...unclusteredPointLayerBatteryWarning} />
      </ReactMapGL.Source>

      {popupInfo && (
        <ReactMapGL.Popup
          anchor="top"
          longitude={popupInfo.longitude}
          latitude={popupInfo.latitude}
          onClose={() => setPopupInfo(null)}
        >
          <DeviceView
            longitude={popupInfo.longitude}
            latitude={popupInfo.latitude}
            deviceId={popupInfo.deviceId}
            insightCount={popupInfo.insightCount}
            deviceName={popupInfo.deviceName}
            location={popupInfo.location}
            mostRecentClassName={popupInfo.mostRecentClassName}
            mostRecentTimestamp={popupInfo.mostRecentTimestamp}
            setTab={setTab}
            lastSeen={popupInfo.lastSeen}
            insightWarning={popupInfo.insightWarning}
            spypointCableError={popupInfo.spypointCableError}
            syncWarning={popupInfo.syncWarning}
            batteryLevelWarning={popupInfo.batteryLevelWarning}
            filterManager={filterManager}
            lastOnDeviceTimestamp={popupInfo.lastOnDeviceTimestamp}
            device_battery={popupInfo.device_battery}
          />
        </ReactMapGL.Popup>
      )}

      <ReactMapGL.ScaleControl />
      <ReactMapGL.NavigationControl position="bottom-right" />
      <ReactMapGL.FullscreenControl />
      {!hideDropdown && (
        <div className={classes.mapActionMenu}>
          <Paper className={classes.legend}>
            <div className={classes.topElements}>
              <div className={classes.centerIcons}>
                <MapIcon color="action" />
                <Switch
                  checked={styleChecked}
                  onChange={handleStyleChange}
                  color="default"
                  name="checkedB"
                  inputProps={{ "aria-label": "primary checkbox" }}
                />
                <SatelliteIcon color="action" />
              </div>
              <Button
                className={classes.collapseButton}
                onClick={() => {
                  setCollapseLegend(!collapseLegend)
                }}
              >
                {collapseLegend ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </Button>
            </div>
            {!collapseLegend && (
              <div className={classes.collapsedGroup}>
                <div className={classes.filterContainer}>
                  <CustomTableFilters
                    filterManager={filterManager}
                    data={points}
                    setFilteredData={setFilteredPoints}
                  />
                </div>
                <div className={classes.layers}>
                  <Typography variant="h6">Layer Legend</Typography>
                  <div className={classes.options}>
                    {Object.values(layers).map((layer) => (
                      <FormControlLabel
                        key={layer.id}
                        control={
                          <Checkbox
                            checked={layer.visible}
                            onChange={() => toggleLayerVisibility(layer.id)}
                            name={layer.id}
                          />
                        }
                        label={layer.name}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Paper>
        </div>
      )}
    </ReactMapGL.Map>
  )
}

export default withStyles(styles)(MapView)
